/*
 * SEO KEYWORD MAP — JORNADA DE TRABALHO
 * URL: /jornada-de-trabalho
 * Primary: 44 horas semanais (9.9K vol, KD 27)
 * Secondary: 44 horas semanais são quantas horas por dia (4.4K, KD 15)
 *            40 horas semanais são quantas horas por dia (6.6K, KD 26)
 *            30 horas semanais são quantas horas por dia (4.4K, KD 19)
 * LSI: jornada de trabalho CLT, carga horária semanal, horas por dia,
 *       escala de trabalho, 6 horas por dia, jornada parcial
 * Total addressable: ~25,300/mo
 * Page type: Informational + Calculator
 */
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';

// 6.6667h -> "6h40min", 8h -> "8h" (matches the table below)
const formatHoras = (decimal) => {
  const totalMin = Math.round(decimal * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}min` : `${h}h`;
};

const JornadaTrabalhoPage = () => {
  const [horasSemanais, setHorasSemanais] = useState(44);
  const [diasSemana, setDiasSemana] = useState(5);
  const SABADO = 4; // Saturday hours in the 44h / 6-day CLT pattern

  const result = useMemo(() => {
    if (horasSemanais <= 0 || diasSemana <= 0) return null;
    const horasDiarias = horasSemanais / diasSemana;
    const horasMensais = (horasSemanais / diasSemana) * (diasSemana * 4.33);

    // For 44h/5 days + Saturday pattern
    let distribuicao = '';
    if (horasSemanais === 44 && diasSemana === 5) {
      distribuicao = '8h48min por dia (segunda a sexta)';
    } else if (horasSemanais === 44 && diasSemana === 6) {
      distribuicao = `8h por dia (segunda a sexta) + ${SABADO}h no sábado`;
    } else {
      distribuicao = `${formatHoras(horasDiarias)} por dia`;
    }

    return {
      horasDiarias: formatHoras(horasDiarias),
      horasMensais: horasMensais.toFixed(0),
      divisor: horasSemanais === 44 ? 220 : horasSemanais === 36 ? 180 : horasSemanais === 30 ? 150 : Math.round(horasSemanais * 5),
      distribuicao
    };
  }, [horasSemanais, diasSemana]);

  const jornadaTable = [
    { semanal: 20, diaria5: '4h', diaria6: '3h20min', mensal: 100, tipo: 'Parcial' },
    { semanal: 24, diaria5: '4h48min', diaria6: '4h', mensal: 120, tipo: 'Parcial (turno 6h)' },
    { semanal: 30, diaria5: '6h', diaria6: '5h', mensal: 150, tipo: 'Parcial / Concurso' },
    { semanal: 36, diaria5: '7h12min', diaria6: '6h', mensal: 180, tipo: 'Telefonistas / Saúde' },
    { semanal: 40, diaria5: '8h', diaria6: '6h40min', mensal: 200, tipo: 'Comum (sem sábado)' },
    { semanal: 44, diaria5: '8h48min', diaria6: '8h + 4h sáb', mensal: 220, tipo: 'CLT padrão' },
  ];

  const faqs = [
    {
      question: '44 horas semanais são quantas horas por dia?',
      answer: 'Depende de quantos dias você trabalha. Se trabalha 6 dias (segunda a sábado), são 8 horas de segunda a sexta + 4 horas no sábado. Se trabalha 5 dias (segunda a sexta), são 8 horas e 48 minutos por dia. A CLT permite as duas distribuições.'
    },
    {
      question: '40 horas semanais são quantas horas por dia?',
      answer: '40 horas semanais distribuídas em 5 dias resultam em 8 horas por dia. Essa é a jornada mais comum em empresas que não exigem trabalho aos sábados. Distribuídas em 6 dias, seriam 6 horas e 40 minutos por dia.'
    },
    {
      question: '30 horas semanais são quantas horas por dia?',
      answer: '30 horas semanais em 5 dias resultam em 6 horas por dia. Essa é a jornada de trabalho parcial prevista no Art. 58-A da CLT, comum em concursos públicos e cargos de meio período.'
    },
    {
      question: 'Qual a jornada máxima de trabalho pela CLT?',
      answer: 'A CLT estabelece no Art. 58 que a jornada máxima é de 8 horas diárias e 44 horas semanais. Com horas extras, o limite diário é de 10 horas (8 normais + 2 extras). Algumas categorias têm jornadas diferenciadas por convenção coletiva.'
    },
    {
      question: 'Quem trabalha 44 horas semanais tem que trabalhar no sábado?',
      answer: 'Não necessariamente. A empresa pode distribuir as 44 horas em 5 dias (8h48min por dia, segunda a sexta) sem exigir trabalho no sábado. Isso depende do acordo entre empregador e empregado ou da convenção coletiva da categoria.'
    },
    {
      question: 'Como calcular o divisor de horas?',
      answer: 'O divisor é usado para calcular o valor da hora de trabalho. Para 44h semanais, o divisor é 220 (44 ÷ 6 × 30, já incluindo o descanso semanal remunerado). Para 36h semanais, é 180. Para 30h, é 150. A fórmula do valor da hora é: Salário ÷ Divisor.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>30, 40 e 44 Horas Semanais: Quantas Horas por Dia?</title>
        <meta name="description" content="Descubra quantas horas por dia são 44, 40 e 30 horas semanais. Calculadora de jornada de trabalho CLT com tabela completa e divisor para cálculo de hora." />
        <link rel="canonical" href="https://calculadoradehoras.net.br/jornada-de-trabalho" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="30, 40 e 44 Horas Semanais: Quantas Horas por Dia?" />
        <meta property="og:description" content="Calcule horas por dia para qualquer jornada semanal. Tabela completa + calculadora grátis." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/jornada-de-trabalho" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="30, 40 e 44 Horas Semanais: Quantas Horas por Dia?" />
        <meta name="twitter:description" content="Calcule horas por dia para qualquer jornada semanal. Tabela completa + calculadora grátis." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Jornada de Trabalho","item":"https://calculadoradehoras.net.br/jornada-de-trabalho"}
        ]
      }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Jornada de Trabalho</span>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-dark-text mb-3">
            Jornada de Trabalho: Quantas Horas por Dia?
          </h1>
          <p className="text-lg text-body-text max-w-2xl mx-auto">
            Descubra quantas horas você deve trabalhar por dia com base na sua carga horária semanal. Tabela completa para 44, 40, 36 e 30 horas semanais.
          </p>
          <span className="inline-block mt-2 text-sm text-body-text bg-light-bg px-3 py-1 rounded-full">Conforme a CLT — Atualizado 2026</span>
        </div>

        {/* Quick Answer */}
        <div className="bg-white border-l-4 border-blue-primary rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-heading font-bold text-blue-primary mb-2">44 Horas Semanais São Quantas Horas por Dia?</h2>
          <p className="text-2xl font-heading font-extrabold text-dark-text">8h por dia + 4h no sábado (6 dias) ou 8h48min por dia (5 dias)</p>
          <p className="text-body-text mt-2">A CLT permite distribuir as <strong>44 horas semanais</strong> de diferentes formas: em 6 dias (8h + 4h sábado) ou em 5 dias (8h48min cada).</p>
        </div>

        {/* Calculator */}
        <div className="bg-white rounded-xl border-2 border-border-gray p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Calculadora de Jornada de Trabalho</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-body-text mb-2">Horas Semanais</label>
              <select value={horasSemanais} onChange={(e) => setHorasSemanais(Number(e.target.value))} className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none">
                <option value={20}>20 horas</option>
                <option value={24}>24 horas</option>
                <option value={30}>30 horas</option>
                <option value={36}>36 horas</option>
                <option value={40}>40 horas</option>
                <option value={44}>44 horas (CLT padrão)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-body-text mb-2">Dias Trabalhados por Semana</label>
              <select value={diasSemana} onChange={(e) => setDiasSemana(Number(e.target.value))} className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none">
                <option value={5}>5 dias (segunda a sexta)</option>
                <option value={6}>6 dias (segunda a sábado)</option>
              </select>
            </div>
          </div>

          {result && (
            <div className="mt-6 bg-light-bg border-l-4 border-success-green rounded-lg p-5">
              <p className="text-sm font-semibold text-body-text uppercase tracking-wide mb-1">Resultado</p>
              <p className="text-2xl font-heading font-bold text-dark-text">{result.distribuicao}</p>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-xs text-body-text">Horas/dia</p>
                  <p className="text-lg font-bold text-dark-text">{result.horasDiarias}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-body-text">Horas/mês</p>
                  <p className="text-lg font-bold text-dark-text">{`~${result.horasMensais}h`}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-body-text">Divisor</p>
                  <p className="text-lg font-bold text-dark-text">{result.divisor}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        {/* Main Table */}
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Tabela Completa: Horas Semanais × Horas por Dia</h2>
          <p className="text-body-text leading-relaxed mb-4">
            Consulte abaixo as jornadas mais comuns no Brasil, com a distribuição diária em 5 e 6 dias, o total mensal e o tipo de contrato onde são aplicadas.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-light-bg">
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Semanal</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">5 Dias</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">6 Dias</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Divisor</th>
                  <th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Tipo</th>
                </tr>
              </thead>
              <tbody>
                {jornadaTable.map((row, i) => (
                  <tr key={i} className={`hover:bg-light-bg transition-colors ${row.semanal === 44 ? 'bg-blue-50 font-semibold' : ''}`}>
                    <td className="p-3 border-b border-border-gray font-bold text-dark-text">{`${row.semanal}h`}</td>
                    <td className="p-3 border-b border-border-gray text-body-text">{row.diaria5}</td>
                    <td className="p-3 border-b border-border-gray text-body-text">{row.diaria6}</td>
                    <td className="p-3 border-b border-border-gray text-body-text">{row.mensal}</td>
                    <td className="p-3 border-b border-border-gray text-body-text text-sm">{row.tipo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Content */}
        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Jornada de Trabalho na CLT</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A <strong>jornada de trabalho</strong> é o período diário durante o qual o trabalhador está à disposição do empregador. No Brasil, o Art. 58 da CLT define a jornada padrão como <strong>8 horas diárias</strong> e <strong>44 horas semanais</strong>.
            </p>
            <p className="text-body-text leading-relaxed mb-4">
              Essas 44 horas semanais podem ser distribuídas de duas formas principais. Na primeira, o trabalhador cumpre 8 horas de segunda a sexta e 4 horas no sábado. Na segunda, as 44 horas são divididas igualmente em 5 dias, resultando em 8 horas e 48 minutos por dia, sem trabalho no sábado.
            </p>
            <p className="text-body-text leading-relaxed">
              A escolha entre uma e outra distribuição depende do acordo entre empregador e empregado, ou da convenção coletiva da categoria profissional. Ambas são legais e respeitam o limite de 44 horas semanais.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">40 Horas Semanais São Quantas Horas por Dia?</h2>
            <p className="text-body-text leading-relaxed mb-4">
              Quem tem jornada de <strong>40 horas semanais</strong> trabalha <strong>8 horas por dia</strong> de segunda a sexta-feira. Essa carga horária é comum em empresas que adotaram a semana de 5 dias sem compensação no sábado.
            </p>
            <p className="text-body-text leading-relaxed">
              Muitas convenções coletivas preveem a jornada de 40 horas como padrão, especialmente em escritórios e no setor de tecnologia. O divisor para cálculo do valor da hora nesse caso é <strong>200</strong> (40 ÷ 6 × 30).
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">30 Horas Semanais São Quantas Horas por Dia?</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A jornada de <strong>30 horas semanais</strong> resulta em <strong>6 horas por dia</strong> em 5 dias de trabalho. Essa é a chamada <strong>jornada parcial</strong>, prevista no Art. 58-A da CLT, que foi ampliada pela Reforma Trabalhista de 2017.
            </p>
            <p className="text-body-text leading-relaxed">
              A jornada parcial de 30 horas não permite horas extras. Já a jornada parcial de 26 horas permite até 6 horas extras semanais. Essa modalidade é comum em empregos de meio período e em muitos cargos de concurso público.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Outras Jornadas Especiais</h2>
            <p className="text-body-text leading-relaxed mb-4">
              Além das jornadas tradicionais, a CLT prevê regimes especiais para determinadas categorias.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-light-bg rounded-lg p-5">
                <h3 className="font-semibold text-dark-text mb-2">🔄 Escala 12×36</h3>
                <p className="text-sm text-body-text">12 horas de trabalho + 36 horas de descanso. Comum na saúde, segurança e portaria. Prevista no Art. 59-A da CLT.</p>
              </div>
              <div className="bg-light-bg rounded-lg p-5">
                <h3 className="font-semibold text-dark-text mb-2">🏦 6 Horas (Bancários)</h3>
                <p className="text-sm text-body-text">Bancários têm jornada de 6 horas diárias e 30 semanais (Art. 224 CLT), salvo cargos de confiança.</p>
              </div>
              <div className="bg-light-bg rounded-lg p-5">
                <h3 className="font-semibold text-dark-text mb-2">📞 Telefonistas</h3>
                <p className="text-sm text-body-text">Jornada de 6 horas diárias e 36 semanais para telefonistas e operadores de telemarketing (Art. 227 CLT).</p>
              </div>
              <div className="bg-light-bg rounded-lg p-5">
                <h3 className="font-semibold text-dark-text mb-2">✈️ Aeronautas</h3>
                <p className="text-sm text-body-text">Profissionais da aviação têm jornada especial regulada pela Lei 13.475/2017, com limites de voo e descanso específicos.</p>
              </div>
            </div>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/jornada-de-trabalho" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default JornadaTrabalhoPage;
